import { api } from '../../lib/api';

/**
 * Il contratto della pagina Oggi: `GET /admin/oggi`
 * (apps/api/src/modules/admin/admin.oggi.ts).
 *
 * La parte "come stiamo con i soldi" (frase, numeri chiave, problemi gravi)
 * NON sta qui: arriva da `GET /admin/situazione`, lo stesso conto della pagina
 * Situazione (features/admin/situazione/situazioneApi.ts). Qui restano il
 * lavoro in mano e in vendita, le fatture da incassare e i pagamenti in arrivo.
 *
 * Qui NON si fanno conti. Nemmeno una sottrazione: ogni numero che la pagina
 * mostra arriva gia' fatto dal server, perche' il browser ha in mano una
 * risposta paginata e non il database.
 *
 * Le tre nature di un numero, che la pagina scrive accanto alla cifra:
 *   FATTO       soldi gia' passati dal conto (incassati, pagati)
 *   CERTO       esiste un documento (fattura emessa, ricevuta, F24 del
 *               commercialista): l'importo e' quello, la data puo' slittare
 *   PREVISIONE  nessun documento: canoni proiettati, spese fisse, voci
 *               inserite a mano, tasse stimate da noi, valore delle trattative
 */

export type Natura = 'FATTO' | 'CERTO' | 'PREVISIONE';

export interface OggiRigaFattura {
  paymentId: string;
  invoiceId: string;
  numero: string | null;
  cliente: string;
  importo: number;
  dueDate: string | null;
  giorniDiRitardo: number;
  rata: { seq: number; su: number } | null;
}

export interface OggiRigaPagamento {
  fonte: 'FORNITORE' | 'TASSE' | 'CANONE' | 'STIPENDIO' | 'MANUALE';
  id: string;
  controparte: string;
  descrizione: string | null;
  dueDate: string | null;
  importo: number;
  documento: boolean;
}

export interface OggiControllo {
  voci: Array<{ chiave: string; etichetta: string; valore: number; natura: Natura }>;
  totaleNetto: number;
  scartato: Array<{ chiave: string; etichetta: string; valore: number; count: number; regola: string }>;
  totaleLordo: number;
  differenza: number;
  quadra: boolean;
}

export interface OggiSummary {
  today: string;
  history: { cutoffDate: string | null; includeHistory: boolean };
  lavoro: {
    inMano: {
      progetti: { valore: number; count: number; giaFatturato: number };
      trattativeVinte: { valore: number; count: number };
      /** Progetti aperti ma fatturati a rate: il valore sta gia' nei canoni, qui si mostra e non si somma. */
      progettiACanone: { valore: number; count: number };
      /** `aggregati` sta DENTRO `valore`: i canoni senza cliente, che la regola anti-doppione non può controllare. */
      ricorrente: { valore: number; count: number; mesi: number; fino: string; aggregati: { valore: number; count: number } };
      totale: number;
    };
    inVendita: {
      valore: number;
      pesato: number;
      count: number;
      rinnovi: { valore: number; count: number };
    };
  };
  fatture: {
    scadute: { count: number; totale: number; voci: OggiRigaFattura[] };
    prossime: { count: number; totale: number; voci: OggiRigaFattura[] };
  };
  /**
   * I pagamenti da oggi alla fine del mese prossimo, senza lo scaduto (R4).
   * `tasseStima` sta DENTRO `tasse`: la parte che è una stima nostra.
   */
  daPagare: {
    fino: string;
    fornitori: number;
    tasse: number;
    tasseStima: number;
    speseFisse: number;
    stipendi: number;
    manuali: number;
    totale: number;
    count: number;
    voci: OggiRigaPagamento[];
  };
  controllo: OggiControllo;
}

export function oggiQuery() {
  return {
    queryKey: ['admin', 'oggi'] as const,
    queryFn: () => api.get<OggiSummary>('/admin/oggi'),
    // Stesso ritmo dell'overview: e' la stessa cassa, guardata da un'altra parte.
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  };
}

/** Etichetta breve da mettere accanto al numero. */
export const NATURA_LABEL: Record<Natura, string> = {
  FATTO: 'fatto',
  CERTO: 'certo',
  PREVISIONE: 'previsione',
};

/** Che cos'è una riga dei pagamenti in arrivo: nomina la cosa, non la fonte tecnica. */
export const FONTE_LABEL: Record<OggiRigaPagamento['fonte'], string> = {
  FORNITORE: 'Fattura di un fornitore',
  TASSE: 'Tassa',
  CANONE: 'Spesa fissa',
  STIPENDIO: 'Stipendio',
  MANUALE: 'Uscita scritta a mano',
};
